import { Injectable } from '@angular/core';

import { EMPTY, Observable } from 'rxjs';
import { CommissionCredit, InvoiceMail } from '../models';
import { AbstractApiResponse } from '../../shared/models';
import { AbstractApiService } from '../../shared/services';
import { getUriWithPageAndParams } from '../../shared/utilities/urlParams.utility';
import { ParameterBag } from '../../shared/models/ParameterBag.interface';
import { ShippingProvider } from '../../shipping/models';
import { UserRoleService } from '../../shared/services/user-role.service';

@Injectable()
export class CommissionCreditsService {
  constructor(
    private apiService: AbstractApiService,
    private rolesService: UserRoleService
  ) {}

  createCommissionCredit(
    payload: CommissionCredit
  ): Observable<CommissionCredit> {
    return this.rolesService.userHasRoleFilter<CommissionCredit>(
      'ROLE_MWS_COMMISSION_CREDIT_CREATE',
      hasRole =>
        hasRole === false
          ? EMPTY
          : this.apiService.createObject(`/commission_credits`, payload)
    );
  }

  createCommissionCreditFromBroker(payload: {
    broker: string;
  }): Observable<CommissionCredit> {
    return this.rolesService.userHasRoleFilter<CommissionCredit>(
      'ROLE_MWS_COMMISSION_CREDIT_CREATE',
      hasRole =>
        hasRole === false
          ? EMPTY
          : this.apiService.createObject(
              `/commission_credits/from_broker`,
              payload
            )
    );
  }

  readCommissionCredit(iri: string): Observable<CommissionCredit> {
    return this.rolesService.userHasRoleFilter<CommissionCredit>(
      'ROLE_MWS_COMMISSION_CREDIT_VIEW',
      hasRole =>
        hasRole === false ? EMPTY : this.apiService.getObject(iri, true)
    );
  }

  sendMail(
    iri: string,
    payload: InvoiceMail
  ): Observable<AbstractApiResponse | any> {
    return this.rolesService.userHasRoleFilter<AbstractApiResponse>(
      'ROLE_MWS_COMMISSION_CREDIT_EMAIL',
      hasRole =>
        hasRole === false
          ? EMPTY
          : this.apiService.updateObject(iri + '/email', payload, true)
    );
  }

  readCommissionCredits(
    page = 1,
    params?: ParameterBag
  ): Observable<Array<any>> {
    return this.rolesService.userHasRoleFilter<Array<any>>(
      'ROLE_MWS_COMMISSION_CREDIT_LIST',
      hasRole =>
        hasRole === false
          ? EMPTY
          : this.apiService.getObject(
              getUriWithPageAndParams('/commission_credits', page, params)
            )
    );
  }

  readCommissionCreditsChunk(uri: string): Observable<AbstractApiResponse> {
    return this.rolesService.userHasRoleFilter<AbstractApiResponse>(
      'ROLE_MWS_COMMISSION_CREDIT_LIST',
      hasRole =>
        hasRole === false ? EMPTY : this.apiService.getObject(uri, true)
    );
  }

  readCommissionCreditAsPdf(credit: string): Observable<AbstractApiResponse> {
    return this.rolesService.userHasRoleFilter<ShippingProvider>(
      'ROLE_MWS_COMMISSION_CREDIT_VIEW',
      hasRole =>
        hasRole === false
          ? EMPTY
          : this.apiService.getDownloadJson(
              credit + '/preview?disposition=attachment',
              true
            )
    );
  }

  updateCommissionCredit(
    iri: string,
    payload: CommissionCredit
  ): Observable<AbstractApiResponse> {
    return this.rolesService.userHasRoleFilter<AbstractApiResponse>(
      'ROLE_MWS_COMMISSION_CREDIT_EDIT',
      hasRole =>
        hasRole === false
          ? EMPTY
          : this.apiService.updateObject(iri, payload, true)
    );
  }

  bookPayOut(
    iri,
    payload: { payoutAt: string }
  ): Observable<AbstractApiResponse> {
    const url = `${iri}/payout_at`;
    return this.rolesService.userHasRoleFilter<AbstractApiResponse>(
      'ROLE_MWS_COMMISSION_CREDIT_PAYOUT',
      hasRole =>
        hasRole === false
          ? EMPTY
          : this.apiService.patchObject(url, payload, true)
    );
  }

  // SECURITY: no roles found
  bookCommissionCredit(iri: string): Observable<AbstractApiResponse> {
    const url = `${iri}/transitions`;
    const payload = { workflow: 'commission_credit', transition: 'book' };
    return this.apiService.patchObject(url, payload, true);
  }

  deleteCommissionCredit(iri: string): Observable<AbstractApiResponse> {
    return this.rolesService.userHasRoleFilter<ShippingProvider>(
      'ROLE_MWS_COMMISSION_CREDIT_DELETE',
      hasRole =>
        hasRole === false ? EMPTY : this.apiService.deleteObject(iri, true)
    );
  }
}
